import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { Card } from './styles';

import NoData from '../../../../../components/NoData';

import download from '../../../../../assets/images/icons/download.svg';
import DashboardService from '../../../../../services/affreightment/DashboardService';
import formatDate from '../../../../../utils/formatDate';
import ExportXlsx from '../../../../../utils/ExportXlsx';
import { useAppContext } from '../../../../../contexts/auth';
import useApiCall from '../../../../../hooks/useApiCall';
import { AffreightmentRelResponse, VtUsageType } from '../../types';

interface VtUsageCard {
  dashboardVtUsageData: VtUsageType;
  selectedWorkplace: { value: number, label: string };
}

export default function VtUsageCard({ dashboardVtUsageData, selectedWorkplace }: VtUsageCard) {
  const currentDate = new Date().toJSON().slice(0, 10);

  const { userData, selectedCompany } = useAppContext();
  const { userCode, userProfile } = userData;
  const companyCode = selectedCompany.value;

  const { apiCall } = useApiCall();

  const downloadRel = useCallback(async () => {
    await toast.promise(apiCall({
      apiToCall: DashboardService.getRelsData,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode },
      reqBody: JSON.stringify({
        codViagem: 0,
        codRelatorio: 6,
        dataViagens: currentDate,
        mes: currentDate.split('-')[1],
        ano: currentDate.split('-')[0],
        dataPesquisa: currentDate,
      }),
      actionAfterResponse: (response: AffreightmentRelResponse) => {
        if (response.usuVT) {
          const usuVtRelData = response.usuVT.map((data) => ({
            'Local de trabalho': data.locTrab,
            CPF: data.cpf,
            Nome: data.nome,
            'Ultima linha ida embarcada': data.linhaIda || 'Não foi identificado nenhum embarque',

            'Data ultimo embarque (linha ida)': (data.ultimoAcessoIda == '0001-01-01T00:00:00'
              ? 'Não foi identificado nenhum embarque'
              : formatDate(data.ultimoAcessoIda)),

            'Ultima linha volta embarcada': data.linhaVolta || 'Não foi identificado nenhum embarque',

            'Data ultimo embarque (linha volta)': (data.ultimoAcessoVolta == '0001-01-01T00:00:00'
              ? 'Não foi identificado nenhum embarque'
              : formatDate(data.ultimoAcessoVolta)),

            'Valor VT': data.valorVT.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          }));
          ExportXlsx({
            data: usuVtRelData,
            filename: `Relatório usuários de VT ${formatDate(currentDate)}`,
          });
        }

        if (!response.usuVT) {
          toast.error('Não há dados para serem baixados');
        }
      },
      catchMessage: 'Não foi possível baixar o relatório de usuários de VT'
    }), {
      pending: 'Sua planilha está sendo baixada',
      success: 'Sua planilha foi baixada!',
      error: 'Não foi possível baixar sua planilha',
    });
  }, [apiCall, companyCode, currentDate, userCode, userProfile]);

  return (
    <>
      <Card>
        <div className="card-title">
          <div>Usam VT</div>
          <img src={download} alt="download" title="Baixar relatório excel" onClick={downloadRel} />
        </div>

        {dashboardVtUsageData && (
          <>
            <div className="card-main">
              <div>
                {dashboardVtUsageData.percUsaVT}
                {' '}
              %
              </div>
              <small>
                {(dashboardVtUsageData.percUsaVT == 1 ? 'Usa VT' : 'Usam VT')}
              </small>
            </div>

            <footer>
              {(!dashboardVtUsageData.VTBaixou) && (
                <>
                  <div className="porcentagem-negativa">
                +
                    {' '}
                    {dashboardVtUsageData.percVT}
                %
                  </div>
                  <div>
                Em relação à média do mês anterior
                  </div>
                </>
              )}
              {(dashboardVtUsageData.VTBaixou) && (
                <>
                  <div className="porcentagem-positiva">
                -
                    {' '}
                    {dashboardVtUsageData.percVT}
                %
                  </div>
                  <div>
                Em relação à média do mês anterior
                  </div>
                </>
              )}
            </footer>
          </>
        )}

        {(!dashboardVtUsageData) && (
          <NoData
            icon="emptyBox"
            label={(
              <>
              O local de trabalho
                {' '}
                <strong>{selectedWorkplace.label}</strong>
                {' '}
              não possui dados sobre usuários de VT.
              </>
            )}
          />
        )}
      </Card>
    </>
  );
}
